import {
  Component,
  inject,
  Input,
  OnInit,
  QueryList,
  ViewChildren
} from '@angular/core';
import { ColumnTypeUtils } from '@app/shared/util/column-type-utils';
import { isActionOnJedinicaDisabled } from '@app/shared/util/jedinica-gradiva-security-utils';
import {
  ActionComponent,
  DynamicComponent,
  InputControlData,
  Item,
  ListItemProviderDirective,
  NtapGlobalConstants,
  Util,
  View
} from '@ntap/core';
import {
  ColumnInfo,
  PrimeNGListActionBarComponent,
  PrimeNGTableComponent
} from '@ntap/primeng';
import { combineLatest, map, Observable, startWith } from 'rxjs';
import { TableMetaWithDataKey } from '../editable-table.component/editable-table.component';
import { AuthService } from '../login.component';

export const KEY_IS_OPENITEM = 'isOpenItemColumn';
const VIEWS_OVERRIDE_ACTION_OPEN_TYPE = [
  'agenti_veza_readonly',
  'registar_arhiva_i_posjednika'
];

@Component({
  selector: 'app-ntap-list',
  templateUrl: './ntap-list.component.html',
  styleUrls: ['./ntap-list.component.scss']
})
export class NtapListComponent extends PrimeNGTableComponent implements OnInit {
  @Input() quickDataEntry = false;

  @Input() inplaceEditing = false;

  @Input() dokumentacija = false;

  @Input() quickDataEntryInputs: Observable<InputControlData[]>;

  @Input()
  public readOnly = false;

  @Input() openAkcijaMarker = 'open';

  tableMetaWithDataKey$: Observable<TableMetaWithDataKey>;
  tableMetaWithDataKey: TableMetaWithDataKey;

  @ViewChildren('rowActionBar')
  dynamicActionToolbars: QueryList<DynamicComponent>;

  private authService = inject(AuthService);
  readonly defaultRowsPageOptions = [15, 25, 50, 100];

  rowsPerPageOptions$ = this.authService.validUser$.pipe(
    map((u) =>
      this.authService.userHasRole(u, 'Superuser')
        ? [...this.defaultRowsPageOptions, ...[1000]]
        : this.defaultRowsPageOptions
    ),
    startWith(this.defaultRowsPageOptions)
  );

  ngOnInit() {
    this.tableMetaWithDataKey$ = combineLatest([
      this.listViewProvider.view$,
      this.tableMeta$
    ]).pipe(
      map(([view, tableMeta]) => {
        const idAttr = Util.getFullAttrName(
          view.$mainType,
          NtapGlobalConstants.ATTR_id
        );
        tableMeta.columns.forEach((column: any) => {
          column.searchable =
            view.searchable_columns.indexOf(column.name) !== -1;
          column[KEY_IS_OPENITEM] = this.isOpenItemColumn(column.name, view);
        });
        const showHeader =
          tableMeta.columns.some((c: any) => c.searchable) ||
          tableMeta.columns.some((c: ColumnInfo) => c.sortable);
        const metaWithDataKey = Object.assign(
          { dataKey: idAttr, showHeader: showHeader, view: view },
          tableMeta
        );
        return metaWithDataKey;
      })
    );
  }

  readOnly$(item: Item) {
    return this.authService.validUser$.pipe(
      map((user) => {
        return isActionOnJedinicaDisabled(item, user).disabled;
      })
    );
  }

  isOpenItemColumn(colName: string, view: View) {
    return (
      colName.indexOf('naziv') > -1 && !!this.hasOpenActions(colName, view)
    );
  }

  private hasOpenActions(colName: string, view: View) {
    return view.actions.find((a: any) => this.isOpenAkcija(a, colName, view));
  }

  private isOpenAkcija(action: any, colName: string, view: View): boolean {
    const openItemTypeParam = action['$openItemType'];
    return (
      this.hasAkcijaOpenMarker(action) &&
      !!openItemTypeParam &&
      (VIEWS_OVERRIDE_ACTION_OPEN_TYPE.indexOf(view.name) > -1 ||
        colName.indexOf(openItemTypeParam) > -1)
    );
  }

  openItem(event: Event, listItemProvider: ListItemProviderDirective) {
    event.preventDefault();
    const actionComp = this.findItemFirstOpenActionComponent(listItemProvider);
    if (actionComp) {
      actionComp.execute();
    }
  }

  private findItemFirstOpenActionComponent(
    listItemProvider: ListItemProviderDirective
  ): ActionComponent | undefined {
    return this.dynamicActionToolbars
      .map((tb) => tb.getViewComponent() as PrimeNGListActionBarComponent)
      .flatMap((ab) => ab.dynamicComponents.toArray())
      .map((dc) => dc.getViewComponent() as ActionComponent)
      .find(
        (ac: ActionComponent) =>
          this.hasAkcijaOpenMarker(ac.action) &&
          (ac as any).itemProvider === listItemProvider
      );
  }

  private hasAkcijaOpenMarker(action: any): boolean {
    return action.name.indexOf(this.openAkcijaMarker) > -1;
  }

  isIconColumn(columnName: string): boolean {
    return (
      columnName === 'en~jedinica_gradiva~lifecycle_state_id' ||
      columnName === 'en~jedinica_gradiva~razina'
    );
  }

  isLockedColumn(columnName: string): boolean {
    return ColumnTypeUtils.isLockedColumn(columnName);
  }

  isPublicColumn(columnName: string): boolean {
    return ColumnTypeUtils.isPublicColumn(columnName);
  }
}
